import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { analyticsService, AnalyticsData } from '../../services/analytics.service';
import { Loading } from '../../components/ui/Loading';

export const AnalyticsPage = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await analyticsService.getAnalytics();
      setAnalytics(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;
  if (!analytics) return <div>No data available</div>;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
        <p className="text-gray-600">View platform analytics and reports</p>
      </div>

      {/* Member Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Total Active Members</div>
          <div className="text-3xl font-bold text-blue-600">{analytics.memberCount.total}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Individual</div>
          <div className="text-3xl font-bold text-green-600">
            {analytics.memberCount.byType.INDIVIDUAL || 0}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Family</div>
          <div className="text-3xl font-bold text-purple-600">
            {analytics.memberCount.byType.FAMILY || 0}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Corporate</div>
          <div className="text-3xl font-bold text-orange-600">
            {analytics.memberCount.byType.CORPORATE || 0}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Service Usage */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Service Usage</h2>
          <div className="mb-4">
            <div className="text-2xl font-bold text-blue-600">{analytics.serviceUsage.totalRequests}</div>
            <div className="text-sm text-gray-500">Total Requests</div>
          </div>
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">By Type:</h3>
            {analytics.serviceUsage.byType.length === 0 ? (
              <p className="text-gray-500 text-center">No service usage data</p>
            ) : (
              analytics.serviceUsage.byType.map((service) => (
                <div key={service.type} className="flex justify-between items-center">
                  <span className="text-gray-700">{service.type}</span>
                  <span className="font-semibold text-blue-600">{service.count}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Membership Type Comparison */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Membership Comparison</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-700">Corporate</span>
                <span className="font-semibold">{analytics.comparison.corporate.memberCount} members</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{
                    width: `${(analytics.comparison.corporate.memberCount / analytics.memberCount.total) * 100}%`
                  }}
                />
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {analytics.comparison.corporate.serviceRequests} requests
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-700">Individual</span>
                <span className="font-semibold">{analytics.comparison.individual.memberCount} members</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full"
                  style={{
                    width: `${(analytics.comparison.individual.memberCount / analytics.memberCount.total) * 100}%`
                  }}
                />
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {analytics.comparison.individual.serviceRequests} requests
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-700">Family</span>
                <span className="font-semibold">{analytics.comparison.family.memberCount} members</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full"
                  style={{
                    width: `${(analytics.comparison.family.memberCount / analytics.memberCount.total) * 100}%`
                  }}
                />
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {analytics.comparison.family.serviceRequests} requests
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Renewal Analytics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Renewal Analytics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-1">Expiring This Month</div>
            <div className="text-2xl font-bold text-blue-600">{analytics.renewals.expiringThisMonth}</div>
          </div>
          <div className="border rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-1">Expiring Next Month</div>
            <div className="text-2xl font-bold text-yellow-600">{analytics.renewals.expiringNextMonth}</div>
          </div>
          <div className="border rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-1">Churn Rate</div>
            <div className="text-2xl font-bold text-red-600">{analytics.renewals.churnRate.toFixed(2)}%</div>
          </div>
        </div>
        
        {analytics.renewals.upcomingRenewals.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Upcoming Renewals (Next 60 Days)</h3>
            <div className="space-y-2">
              {analytics.renewals.upcomingRenewals.slice(0, 5).map((renewal) => (
                <div key={renewal.memberId} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <div>
                    <div className="font-medium">{renewal.memberName}</div>
                    <div className="text-xs text-gray-500">{renewal.membershipType}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">{renewal.daysUntilExpiry} days</div>
                    <div className="text-xs text-gray-500">
                      {new Date(renewal.validUntil).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
